import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    IconButton,
    InputAdornment,
    TextField,
    Typography,
    type SxProps,
    type Theme,
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { useAuth } from '../lib/auth';
import { getSupabase } from '../lib/supabase';

/**
 * Where an invite or password-reset email lands.
 *
 * Both links are generated by Supabase Auth (inviteUserByEmail and
 * resetPasswordForEmail in apps/api/src/services/staff-admin.ts) with
 * redirectTo pointing here. The client runs the implicit flow, so the link
 * arrives as `#access_token=...&type=invite|recovery`; supabase-js consumes
 * that fragment while it initialises and turns it into a real session before
 * getSession() resolves. So by the time useAuth() reports a session, the
 * fragment is already spent and there is nothing to parse here.
 *
 * That session is the whole reason this page has to exist. An invite link
 * signs the staff member in *before* they have a password — without somewhere
 * to set one they would land on the dashboard with an account they can never
 * log into again.
 *
 * It doubles as an ordinary change-password screen for anyone already signed
 * in, which is why it does not insist on having arrived from a link.
 */

// Matching pages/Login.tsx, which borrows icu-gps's greys so the two products
// read as one family.
const TEXT_LIGHT = '#ecf0f1';
const TEXT_MUTED = '#95a5a6';
const BORDER = '#7f8c8d';

/** Supabase rejects anything shorter; this is stricter than the project's
 *  minimum_password_length of 6, deliberately, and the server still has the
 *  final say. */
const MIN_LENGTH = 8;

const fieldLabelSx: SxProps<Theme> = {
    color: 'primary.main',
    fontSize: '0.75rem',
    display: 'block',
    mb: 1,
};

const textFieldSx: SxProps<Theme> = {
    '& .MuiOutlinedInput-root': { bgcolor: 'background.default', color: TEXT_LIGHT, borderRadius: '8px' },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: BORDER },
    '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': { borderColor: TEXT_MUTED },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'primary.main' },
    '& .MuiInputBase-input': { color: TEXT_LIGHT },
};

export function SetPassword(): React.JSX.Element {
    const { session, loading, error: configError } = useAuth();
    const navigate = useNavigate();
    const [password, setPassword] = useState('');
    const [confirm, setConfirm] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState(false);

    // Straight to the app once the password is set -- they are already signed
    // in on the session the link established, so there is nothing to log into.
    useEffect(() => {
        if (!done) {
            return;
        }
        const t = setTimeout(() => {
            void navigate('/', { replace: true });
        }, 1_800);
        return () => {
            clearTimeout(t);
        };
    }, [done, navigate]);

    const tooShort = password !== '' && password.length < MIN_LENGTH;
    const mismatch = confirm !== '' && password !== confirm;
    const submittable = password.length >= MIN_LENGTH && password === confirm && !busy;

    const onSubmit = (e: FormEvent): void => {
        e.preventDefault();
        if (!submittable) {
            return;
        }
        setError(null);
        setBusy(true);
        void (async () => {
            try {
                const sb = await getSupabase();
                const { error: updateError } = await sb.auth.updateUser({ password });
                if (updateError !== null) {
                    // Supabase's own wording is specific and useful here
                    // ("should be different from the old password", and so on),
                    // unlike a failed sign-in where being vague is the point.
                    setError(updateError.message);
                    return;
                }
                setDone(true);
            } catch (err: unknown) {
                setError(err instanceof Error ? err.message : 'Could not set your password. Please try again.');
            } finally {
                setBusy(false);
            }
        })();
    };

    const shell = (children: React.JSX.Element): React.JSX.Element => (
        <Box
            sx={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'center',
                bgcolor: 'background.default',
                p: 4,
                pt: 'calc(32px + env(safe-area-inset-top, 0px))',
            }}
        >
            <Box sx={{ width: '100%', maxWidth: 400 }}>
                <Box sx={{ textAlign: 'center', mb: 3 }}>
                    <Box
                        component="img"
                        src="/silverline-logo.png"
                        alt="Silverline Construction Group"
                        sx={{ width: '100%', maxWidth: 220, height: 'auto', my: 2 }}
                    />
                </Box>
                {children}
            </Box>
        </Box>
    );

    if (loading) {
        return shell(
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={28} />
            </Box>,
        );
    }

    if (configError !== null) {
        return shell(<Alert severity="warning">{configError}</Alert>);
    }

    // No session means the link was already used, has expired, or the page was
    // opened on its own. Nothing here can recover that, so say so plainly and
    // give them the two things that can: Tamara, or a fresh sign-in.
    if (session === null) {
        return shell(
            <Box>
                <Typography variant="h6" sx={{ color: TEXT_LIGHT, mb: 1.5, textAlign: 'center' }}>
                    This link has expired
                </Typography>
                <Typography variant="body2" sx={{ color: TEXT_MUTED, mb: 3, textAlign: 'center', lineHeight: 1.7 }}>
                    Invite and password reset links can only be used once, and expire after a while.
                    Ask for a new one, or call Tamara on{' '}
                    <Box component="a" href="tel:+61424869839" sx={{ color: 'primary.main', textDecoration: 'none' }}>
                        0424&nbsp;869&nbsp;839
                    </Box>
                    .
                </Typography>
                <Button
                    fullWidth
                    variant="contained"
                    onClick={() => {
                        void navigate('/login', { replace: true });
                    }}
                    sx={{
                        bgcolor: 'primary.main',
                        color: 'background.default',
                        p: 1.5,
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        borderRadius: '8px',
                    }}
                >
                    Back to sign in
                </Button>
            </Box>,
        );
    }

    if (done) {
        return shell(
            <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h6" sx={{ color: TEXT_LIGHT, mb: 1.5 }}>
                    Password set
                </Typography>
                <Typography variant="body2" sx={{ color: TEXT_MUTED, mb: 3 }}>
                    Taking you to the portal&hellip;
                </Typography>
                <CircularProgress size={24} />
            </Box>,
        );
    }

    return shell(
        <Box>
            <Typography variant="h6" sx={{ color: TEXT_LIGHT, mb: 1, textAlign: 'center' }}>
                Choose your password
            </Typography>
            <Typography variant="body2" sx={{ color: TEXT_MUTED, mb: 3, textAlign: 'center' }}>
                {session.user.email ?? ''}
            </Typography>

            {error !== null ? (
                <Typography
                    sx={{
                        color: '#e74c3c',
                        mb: 2,
                        p: 1,
                        bgcolor: 'rgba(231, 76, 60, 0.1)',
                        borderRadius: 1,
                        textAlign: 'center',
                    }}
                >
                    {error}
                </Typography>
            ) : null}

            <Box component="form" onSubmit={onSubmit}>
                {/* Carries the account for password managers, which otherwise
                    offer to save the new password against no username. */}
                <input type="hidden" name="username" autoComplete="username" value={session.user.email ?? ''} />

                <Box sx={{ mb: 1.5 }}>
                    <Typography component="label" htmlFor="password" sx={fieldLabelSx}>
                        New password *
                    </Typography>
                    <TextField
                        id="password"
                        required
                        fullWidth
                        type={showPassword ? 'text' : 'password'}
                        name="password"
                        autoComplete="new-password"
                        autoFocus
                        placeholder={`At least ${String(MIN_LENGTH)} characters`}
                        value={password}
                        error={tooShort}
                        helperText={tooShort ? `At least ${String(MIN_LENGTH)} characters.` : ' '}
                        onChange={(e) => {
                            setPassword(e.target.value);
                        }}
                        slotProps={{
                            input: {
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={() => {
                                                setShowPassword((v) => !v);
                                            }}
                                            edge="end"
                                            size="small"
                                            sx={{ color: TEXT_MUTED }}
                                            aria-label={showPassword ? 'Hide password' : 'Show password'}
                                        >
                                            {showPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            },
                        }}
                        sx={textFieldSx}
                    />
                </Box>

                <Box sx={{ mb: 2 }}>
                    <Typography component="label" htmlFor="confirm" sx={fieldLabelSx}>
                        Confirm password *
                    </Typography>
                    <TextField
                        id="confirm"
                        required
                        fullWidth
                        type={showPassword ? 'text' : 'password'}
                        name="confirm"
                        autoComplete="new-password"
                        placeholder="Enter it again"
                        value={confirm}
                        error={mismatch}
                        helperText={mismatch ? 'The two passwords do not match.' : ' '}
                        onChange={(e) => {
                            setConfirm(e.target.value);
                        }}
                        sx={textFieldSx}
                    />
                </Box>

                <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    disabled={!submittable}
                    sx={{
                        bgcolor: 'primary.main',
                        color: 'background.default',
                        p: 1.5,
                        fontSize: '0.9rem',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        borderRadius: '8px',
                        '&:hover': { bgcolor: '#2980b9' },
                        '&.Mui-disabled': { bgcolor: BORDER },
                    }}
                >
                    {busy ? <CircularProgress size={22} color="inherit" /> : 'Set password'}
                </Button>
            </Box>

            <Typography
                variant="caption"
                sx={{ color: BORDER, textAlign: 'center', display: 'block', width: '100%', mt: 4 }}
            >
                Trouble getting in? Call Tamara on{' '}
                <Box component="a" href="tel:+61424869839" sx={{ color: TEXT_MUTED, textDecoration: 'none' }}>
                    0424&nbsp;869&nbsp;839
                </Box>
            </Typography>
        </Box>,
    );
}
